// Outbound — fulfilling real orders out of a warehouse.
//   Orders          the pick/pack queue: what hasn't shipped yet + whether the stock is actually here
//   Dispatch        book the courier (same engine as the Orders module) + the manual dispatch register
//   Label Printing  bulk labels for anything already booked
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Printer, Truck, ClipboardList, PackageCheck, AlertTriangle } from 'lucide-react';
import { api, API_BASE_URL, tokenStore, downloadBlob, blobErrorMessage } from '@/api/client';
import {
  Badge, WmsCard, WmsPageBar, WmsSearch,
  useActiveWarehouses, useSearchPaged, useWmsMerchants, useWmsViewOnly,
} from './WmsShared';

/** Open a print-ready document returned by the orders print endpoints. */
async function printDoc(kind: 'labels' | 'invoices' | 'waybills', orderIds: string[], opts?: { size?: string; template?: string }) {
  if (orderIds.length === 0) return;
  const body: any = { orderIds };
  if (kind === 'labels') { body.size = opts?.size; body.template = opts?.template; }
  const r = await fetch(`${API_BASE_URL}/orders/${kind}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${tokenStore.get()}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) { alert((await r.text()).slice(0, 300) || 'Could not generate the document'); return; }
  const html = await r.text();
  const w = window.open('', '_blank');
  if (!w) { alert('Pop-up blocked — allow pop-ups for this site to print.'); return; }
  w.document.open(); w.document.write(html); w.document.close();
}

/** Download the selected labels as a ZIP of PDFs (one per order, named by AWB/LR). */
async function downloadLabelZip(orderIds: string[], opts?: { size?: string; template?: string }): Promise<string | null> {
  const r = await fetch(`${API_BASE_URL}/orders/labels/zip`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${tokenStore.get()}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ orderIds, size: opts?.size, template: opts?.template }),
  });
  if (!r.ok) {
    let msg = `Failed (${r.status})`;
    try { const j = await r.json(); msg = j?.message ?? msg; } catch { /* not json */ }
    return msg;
  }
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `labels-${new Date().toISOString().slice(0, 10)}.zip`; a.click();
  URL.revokeObjectURL(url);
  return null;
}

/** Shared warehouse + merchant filter bar. A warehouse is required before stock can be judged. */
function OutboundFilters({ warehouseId, setWarehouseId, merchantId, setMerchantId }: any) {
  const { data: warehouses = [] } = useActiveWarehouses();
  const { data: merchants = [] } = useWmsMerchants();
  return (
    <>
      <select value={warehouseId ?? ''} onChange={(e) => setWarehouseId(e.target.value ? Number(e.target.value) : '')} className="border rounded px-2 py-1.5 text-sm">
        <option value="">Warehouse — all</option>
        {(warehouses as any[]).map((w) => <option key={w.id} value={w.id}>#{w.id} · {w.name}</option>)}
      </select>
      <select value={merchantId ?? ''} onChange={(e) => setMerchantId(e.target.value ? Number(e.target.value) : '')} className="border rounded px-2 py-1.5 text-sm">
        <option value="">Merchant — all</option>
        {(merchants as any[]).map((m) => <option key={m.id} value={m.id}>#{m.id} · {m.companyName}</option>)}
      </select>
    </>
  );
}

function useQueue(params: any) {
  return useQuery({
    queryKey: ['wms-outbound-queue', params],
    queryFn: async () => (await api.get('/wms/outbound/queue', { params })).data as { items: any[]; total: number },
  });
}

// ─────────────────────────── ORDERS (pick / pack) ───────────────────────────

export function OutboundOrdersTab() {
  const qc = useQueryClient();
  const viewOnly = useWmsViewOnly();
  const [warehouseId, setWarehouseId] = useState<any>('');
  const [merchantId, setMerchantId] = useState<any>('');
  const [orderType, setOrderType] = useState('');
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<string | null>(null);

  const params: any = { pageSize: 300 };
  if (warehouseId) params.warehouseId = warehouseId;
  if (merchantId) params.merchantId = merchantId;
  if (orderType) params.orderType = orderType;
  const { data, isLoading } = useQueue(params);
  const rows = data?.items ?? [];

  const { q, setQ, page, setPage, total, pageRows } = useSearchPaged(
    rows,
    (o: any, t) => [o.orderNo, o.customerName, o.customerCity, o.customerPincode, o.merchantName, o.statusName]
      .some((v) => String(v ?? '').toLowerCase().includes(t)),
  );
  const refresh = () => qc.invalidateQueries({ queryKey: ['wms-outbound-queue'] });
  const toggle = (id: string) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allOnPage = pageRows.length > 0 && pageRows.every((o: any) => sel.has(o.id));

  const setStatus = useMutation({
    mutationFn: async (status: 'PICKED' | 'PACKED' | 'PENDING') =>
      (await api.post('/wms/outbound/pick-status', { orderIds: [...sel], warehouseId, status })).data,
    onSuccess: (_r, status) => { setSel(new Set()); refresh(); alert(`Marked ${status.toLowerCase()}.`); },
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Could not update'),
  });

  return (
    <WmsCard
      title={<span className="flex items-center gap-2"><ClipboardList size={16} /> Outbound Orders — pick &amp; pack queue</span>}
      actions={
        <>
          <WmsSearch value={q} onChange={setQ} placeholder="Search order / customer / city" className="w-64" />
          <OutboundFilters warehouseId={warehouseId} setWarehouseId={setWarehouseId} merchantId={merchantId} setMerchantId={setMerchantId} />
          <select value={orderType} onChange={(e) => setOrderType(e.target.value)} className="border rounded px-2 py-1.5 text-sm">
            <option value="">B2C + B2B</option><option value="B2C">B2C</option><option value="B2B">B2B</option>
          </select>
          {/* A bare <a href> sends NO Authorization header, so this route — which is behind the JWT
              guard and a Warehouses → Dispatch export permission — answered 401 and the file never
              arrived, for everyone, however their role was set up. It also carried no query string,
              so it would have exported the UNfiltered queue while the screen showed a filtered one.
              Routed through downloadBlob: authenticated, filtered to match the screen, 15-minute
              timeout, and a real message on failure. */}
          <button
            type="button"
            onClick={async () => {
              try {
                const qs = new URLSearchParams(
                  Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '') as any,
                ).toString();
                await downloadBlob(
                  `/wms/outbound/queue/export${qs ? `?${qs}` : ''}`,
                  `wms-outbound-queue-${new Date().toISOString().slice(0, 10)}.csv`,
                );
              } catch (e: any) {
                alert(await blobErrorMessage(e, 'Could not export the outbound queue'));
              }
            }}
            className="border px-3 py-1.5 rounded text-sm text-slate-600 hover:bg-slate-50"
          >
            Export
          </button>
        </>
      }
    >
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <span className="text-xs text-slate-500">
          Orders from WMS-enabled merchants that have not shipped yet. <b>Stock</b> compares each SKU against the selected warehouse.
        </span>
        {!viewOnly && sel.size > 0 && (
          <div className="ml-auto flex items-center gap-2">
            {!warehouseId && <span className="text-xs text-amber-600">Pick a warehouse first</span>}
            <button onClick={() => setStatus.mutate('PICKED')} disabled={!warehouseId || setStatus.isPending} className="border border-emerald-500 text-emerald-700 px-3 py-1.5 rounded text-sm disabled:opacity-50">Mark Picked ({sel.size})</button>
            <button onClick={() => setStatus.mutate('PACKED')} disabled={!warehouseId || setStatus.isPending} className="bg-emerald-600 text-white px-3 py-1.5 rounded text-sm disabled:opacity-50">Mark Packed ({sel.size})</button>
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-emerald-50"><tr>
            {!viewOnly && <th className="p-2 w-8"><input type="checkbox" checked={allOnPage} onChange={() => setSel(allOnPage ? new Set() : new Set(pageRows.map((o: any) => o.id)))} /></th>}
            <th className="p-2 text-left">Order No</th><th className="text-left">Type</th>
            {!viewOnly && <th className="text-left">Merchant</th>}
            <th className="text-left">Customer</th><th className="text-left">Destination</th>
            <th className="text-left">Courier</th><th className="text-right">Value</th><th className="text-left">Stock</th>
            <th>Order Status</th><th>Pick</th>
          </tr></thead>
          <tbody>
            {isLoading ? <tr><td colSpan={11} className="text-center py-6 text-slate-400">Loading…</td></tr>
              : total === 0 ? <tr><td colSpan={11} className="text-center py-6 text-slate-400">{q ? 'No matches' : 'Nothing waiting to go out'}</td></tr>
              : pageRows.map((o: any) => (
                <>
                  <tr key={o.id} className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => setExpanded(expanded === o.id ? null : o.id)}>
                    {!viewOnly && <td className="p-2" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={sel.has(o.id)} onChange={() => toggle(o.id)} /></td>}
                    <td className="p-2 font-mono text-xs">{o.orderNo}</td>
                    <td className="text-xs">{o.orderType}</td>
                    {!viewOnly && <td className="text-xs">{o.merchantName}</td>}
                    <td className="text-xs">{o.customerName}</td>
                    <td className="text-xs">{o.customerCity}{o.customerPincode ? ` · ${o.customerPincode}` : ''}</td>
                    <td className="text-xs">{o.courierName ?? <span className="text-red-500">none</span>}</td>
                    <td className="text-right text-xs">{o.deliveryType === 'COD' ? `COD ₹${o.codValue}` : `₹${o.invoiceValue}`}</td>
                    <td className="text-xs">
                      {o.trackedLines === 0
                        ? <span className="text-slate-400" title="No line matched a WMS product, so stock can't be checked">not tracked</span>
                        : o.stockOk
                          ? <span className="text-emerald-700">in stock</span>
                          : <span className="text-red-600 flex items-center gap-1"><AlertTriangle size={12} /> {o.shortLines} short</span>}
                    </td>
                    <td><Badge text={o.statusName} /></td>
                    <td><Badge text={o.pickStatus} /></td>
                  </tr>
                  {expanded === o.id && (
                    <tr key={`${o.id}-x`} className="bg-slate-50 border-t">
                      <td colSpan={11} className="p-3">
                        <div className="text-xs font-medium mb-2">Lines</div>
                        <table className="text-xs">
                          <thead><tr className="text-slate-500"><th className="text-left pr-6">SKU</th><th className="text-left pr-6">Product</th><th className="text-right pr-6">Ordered</th><th className="text-right pr-6">Available here</th></tr></thead>
                          <tbody>
                            {o.lines.map((l: any, i: number) => (
                              <tr key={i} className={l.available !== null && l.available < l.qty ? 'text-red-600' : ''}>
                                <td className="font-mono pr-6">{l.sku ?? '—'}</td>
                                <td className="pr-6">{l.name ?? '—'}</td>
                                <td className="text-right pr-6">{l.qty}</td>
                                <td className="text-right pr-6">{l.available === null ? 'not tracked' : l.available}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {o.lastBookingError && <div className="mt-2 text-xs text-red-600">Last booking error: {o.lastBookingError}</div>}
                      </td>
                    </tr>
                  )}
                </>
              ))}
          </tbody>
        </table>
      </div>
      <WmsPageBar page={page} total={total} onPage={setPage} />
    </WmsCard>
  );
}

// ─────────────────────────── DISPATCH (book + deduct) ───────────────────────────

export function OutboundDispatchTab() {
  const qc = useQueryClient();
  const viewOnly = useWmsViewOnly();
  const [warehouseId, setWarehouseId] = useState<any>('');
  const [merchantId, setMerchantId] = useState<any>('');
  const [onlyPacked, setOnlyPacked] = useState(true);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<any>(null);

  const params: any = { pageSize: 300 };
  if (warehouseId) params.warehouseId = warehouseId;
  if (merchantId) params.merchantId = merchantId;
  const { data, isLoading } = useQueue(params);
  const all = data?.items ?? [];
  const rows = onlyPacked ? all.filter((o: any) => o.pickStatus === 'PACKED') : all;

  const { q, setQ, page, setPage, total, pageRows } = useSearchPaged(
    rows, (o: any, t) => [o.orderNo, o.customerName, o.merchantName].some((v) => String(v ?? '').toLowerCase().includes(t)),
  );
  const toggle = (id: string) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allOnPage = pageRows.length > 0 && pageRows.every((o: any) => sel.has(o.id));

  const dispatch = useMutation({
    mutationFn: async () => (await api.post('/wms/outbound/dispatch', { orderIds: [...sel], warehouseId })).data,
    onSuccess: (r: any) => {
      setSel(new Set());
      setResult(r);
      qc.invalidateQueries({ queryKey: ['wms-outbound-queue'] });
      qc.invalidateQueries({ queryKey: ['wms-stock'] });
      qc.invalidateQueries({ queryKey: ['wms-dispatch'] });
    },
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Dispatch failed'),
  });

  return (
    <>
      <WmsCard
        title={<span className="flex items-center gap-2"><Truck size={16} /> Dispatch — book the courier &amp; ship</span>}
        actions={
          <>
            <WmsSearch value={q} onChange={setQ} placeholder="Search order / customer" className="w-64" />
            <OutboundFilters warehouseId={warehouseId} setWarehouseId={setWarehouseId} merchantId={merchantId} setMerchantId={setMerchantId} />
          </>
        }
      >
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <span className="text-xs text-slate-500">
            Books each order with its assigned courier (B2C and B2B, the same engine the Orders module uses), then takes the stock out of the chosen warehouse.
          </span>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 ml-auto">
            <input type="checkbox" checked={onlyPacked} onChange={(e) => { setOnlyPacked(e.target.checked); setPage(1); setSel(new Set()); }} />
            Packed only
          </label>
          {!viewOnly && sel.size > 0 && (
            <button onClick={() => {
              if (!confirm(`Book and dispatch ${sel.size} order(s)? This calls the courier and deducts stock — it cannot be undone from here.`)) return;
              dispatch.mutate();
            }} disabled={!warehouseId || dispatch.isPending} className="bg-emerald-600 text-white px-3 py-1.5 rounded text-sm disabled:opacity-50">
              {dispatch.isPending ? 'Booking…' : `Book & Dispatch (${sel.size})`}
            </button>
          )}
          {!viewOnly && sel.size > 0 && !warehouseId && <span className="text-xs text-amber-600">Pick a warehouse first</span>}
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-emerald-50"><tr>
              {!viewOnly && <th className="p-2 w-8"><input type="checkbox" checked={allOnPage} onChange={() => setSel(allOnPage ? new Set() : new Set(pageRows.map((o: any) => o.id)))} /></th>}
              <th className="p-2 text-left">Order No</th><th className="text-left">Type</th>
              {!viewOnly && <th className="text-left">Merchant</th>}
              <th className="text-left">Customer</th><th className="text-left">Courier</th>
              <th className="text-right">Value</th><th className="text-left">Stock</th><th>Pick</th>
            </tr></thead>
            <tbody>
              {isLoading ? <tr><td colSpan={9} className="text-center py-6 text-slate-400">Loading…</td></tr>
                : total === 0 ? <tr><td colSpan={9} className="text-center py-6 text-slate-400">{onlyPacked ? 'Nothing packed and ready to dispatch' : 'Nothing waiting'}</td></tr>
                : pageRows.map((o: any) => (
                  <tr key={o.id} className="border-t hover:bg-slate-50">
                    {!viewOnly && <td className="p-2"><input type="checkbox" checked={sel.has(o.id)} onChange={() => toggle(o.id)} /></td>}
                    <td className="p-2 font-mono text-xs">{o.orderNo}</td>
                    <td className="text-xs">{o.orderType}</td>
                    {!viewOnly && <td className="text-xs">{o.merchantName}</td>}
                    <td className="text-xs">{o.customerName}</td>
                    <td className="text-xs">{o.courierName ?? <span className="text-red-500">none assigned</span>}</td>
                    <td className="text-right text-xs">{o.deliveryType === 'COD' ? `COD ₹${o.codValue}` : `₹${o.invoiceValue}`}</td>
                    <td className="text-xs">{o.trackedLines === 0 ? <span className="text-slate-400">not tracked</span> : o.stockOk ? <span className="text-emerald-700">in stock</span> : <span className="text-red-600">{o.shortLines} short</span>}</td>
                    <td><Badge text={o.pickStatus} /></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <WmsPageBar page={page} total={total} onPage={setPage} />
      </WmsCard>

      {result && <DispatchResult result={result} onClose={() => setResult(null)} />}
    </>
  );
}

/** Honest per-order outcome: what booked, what didn't, and why. */
function DispatchResult({ result, onClose }: any) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-16 z-50" onClick={onClose}>
      <div className="bg-white rounded-lg w-[760px] max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-emerald-700 text-white px-5 py-3 font-semibold flex items-center gap-2">
          <PackageCheck size={18} /> Dispatch result
        </div>
        <div className="p-5 space-y-4 text-sm">
          <div className="flex gap-6">
            <div><div className="text-2xl font-semibold text-emerald-700">{result.dispatched}</div><div className="text-xs text-slate-500">dispatched</div></div>
            <div><div className={`text-2xl font-semibold ${result.failed ? 'text-red-600' : 'text-slate-400'}`}>{result.failed}</div><div className="text-xs text-slate-500">failed</div></div>
          </div>

          {result.booked?.length > 0 && (
            <div>
              <div className="font-medium mb-1">Booked</div>
              <table className="text-xs w-full">
                <thead><tr className="text-slate-500"><th className="text-left">Order</th><th className="text-left">AWB / LR</th></tr></thead>
                <tbody>{result.booked.map((b: any) => <tr key={b.orderId}><td>{b.orderNo}</td><td className="font-mono">{b.awbNo ?? '—'}</td></tr>)}</tbody>
              </table>
            </div>
          )}

          {result.failures?.length > 0 && (
            <div>
              <div className="font-medium mb-1 text-red-700">Failed</div>
              <table className="text-xs w-full">
                <thead><tr className="text-slate-500"><th className="text-left">Order</th><th className="text-left">Stage</th><th className="text-left">Reason</th></tr></thead>
                <tbody>{result.failures.map((f: any, i: number) => (
                  <tr key={i} className="border-t"><td>{f.orderNo ?? f.orderId}</td><td>{f.stage}</td><td className="text-red-600">{f.reason}</td></tr>
                ))}</tbody>
              </table>
              <p className="text-xs text-slate-500 mt-1">
                A <b>stock</b> failure means the courier already accepted the shipment but stock could not be deducted — fix the stock, the booking stands.
              </p>
            </div>
          )}

          {result.stockNotes?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs p-3 rounded">
              <b>Not deducted from stock:</b>
              <ul className="list-disc ml-4 mt-1">{result.stockNotes.map((s: any, i: number) => <li key={i}>{s.orderNo}: {s.note}</li>)}</ul>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t flex justify-end"><button onClick={onClose} className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm">Close</button></div>
      </div>
    </div>
  );
}

// ─────────────────────────── LABEL PRINTING ───────────────────────────

export function LabelPrintingTab() {
  const [warehouseId, setWarehouseId] = useState<any>('');
  const [merchantId, setMerchantId] = useState<any>('');
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [size, setSize] = useState('');
  const [busy, setBusy] = useState(false);

  // Labels only exist once a waybill does, so this lists BOOKED orders (status 1 = Booking Received).
  const params: any = { pageSize: 300, status: 1 };
  if (warehouseId) params.warehouseId = warehouseId;
  if (merchantId) params.merchantId = merchantId;
  const { data, isLoading } = useQueue(params);
  const rows = data?.items ?? [];

  const { q, setQ, page, setPage, total, pageRows } = useSearchPaged(
    rows, (o: any, t) => [o.orderNo, o.awbNo, o.customerName, o.merchantName].some((v) => String(v ?? '').toLowerCase().includes(t)),
  );
  const toggle = (id: string) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allOnPage = pageRows.length > 0 && pageRows.every((o: any) => sel.has(o.id));

  const labelOpts = () => (size ? { size, template: size === '3x2' ? 'B2B_3X2' : undefined } : undefined);
  const run = async (kind: 'labels' | 'invoices' | 'waybills') => {
    setBusy(true);
    try { await printDoc(kind, [...sel], kind === 'labels' ? labelOpts() : undefined); }
    finally { setBusy(false); }
  };
  const runZip = async () => {
    setBusy(true);
    try {
      const err = await downloadLabelZip([...sel], labelOpts());
      if (err) alert(err);
    } finally { setBusy(false); }
  };

  return (
    <WmsCard
      title={<span className="flex items-center gap-2"><Printer size={16} /> Label Printing</span>}
      actions={
        <>
          <WmsSearch value={q} onChange={setQ} placeholder="Search order / AWB / customer" className="w-64" />
          <OutboundFilters warehouseId={warehouseId} setWarehouseId={setWarehouseId} merchantId={merchantId} setMerchantId={setMerchantId} />
        </>
      }
    >
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <span className="text-xs text-slate-500">Booked shipments — select and print. Labels open in a new tab ready for the printer (up to 1000 at a time).</span>
        <select value={size} onChange={(e) => setSize(e.target.value)} className="border rounded px-2 py-1.5 text-sm ml-auto">
          <option value="">Label size — merchant default</option>
          <option value="4x6">4×6</option>
          <option value="3x2">3×2 (B2B)</option>
        </select>
        <button onClick={() => run('labels')} disabled={busy || sel.size === 0} className="bg-emerald-600 text-white px-3 py-1.5 rounded text-sm disabled:opacity-50">Print Labels ({sel.size})</button>
        <button onClick={runZip} disabled={busy || sel.size === 0} title="Download as a ZIP of PDFs — one per order, named by AWB/LR" className="border border-emerald-500 text-emerald-700 px-3 py-1.5 rounded text-sm disabled:opacity-50">{busy ? 'Working…' : 'Labels ZIP (PDF)'}</button>
        <button onClick={() => run('invoices')} disabled={busy || sel.size === 0} className="border border-emerald-500 text-emerald-700 px-3 py-1.5 rounded text-sm disabled:opacity-50">Invoices</button>
        <button onClick={() => run('waybills')} disabled={busy || sel.size === 0} className="border px-3 py-1.5 rounded text-sm text-slate-600 disabled:opacity-50" title="Max 50 at a time">Waybills</button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-emerald-50"><tr>
            <th className="p-2 w-8"><input type="checkbox" checked={allOnPage} onChange={() => setSel(allOnPage ? new Set() : new Set(pageRows.map((o: any) => o.id)))} /></th>
            <th className="p-2 text-left">Order No</th><th className="text-left">AWB / LR</th><th className="text-left">Type</th>
            <th className="text-left">Merchant</th><th className="text-left">Customer</th><th className="text-left">Destination</th><th className="text-left">Courier</th>
          </tr></thead>
          <tbody>
            {isLoading ? <tr><td colSpan={8} className="text-center py-6 text-slate-400">Loading…</td></tr>
              : total === 0 ? <tr><td colSpan={8} className="text-center py-6 text-slate-400">No booked shipments to print</td></tr>
              : pageRows.map((o: any) => (
                <tr key={o.id} className="border-t hover:bg-slate-50">
                  <td className="p-2"><input type="checkbox" checked={sel.has(o.id)} onChange={() => toggle(o.id)} /></td>
                  <td className="p-2 font-mono text-xs">{o.orderNo}</td>
                  <td className="font-mono text-xs">{o.awbNo ?? '—'}</td>
                  <td className="text-xs">{o.orderType}</td>
                  <td className="text-xs">{o.merchantName}</td>
                  <td className="text-xs">{o.customerName}</td>
                  <td className="text-xs">{o.customerCity}</td>
                  <td className="text-xs">{o.courierName ?? '—'}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <WmsPageBar page={page} total={total} onPage={setPage} />
    </WmsCard>
  );
}
